/**
 * Pure per-kind divergence comparators for pinned workspaces (Task 8).
 *
 * A pinned workspace opens as a LINKED tab (Task 9's registry drives the
 * open); the tab shows a "diverged" indicator when its live definition no
 * longer matches the pin's stored payload. This module is the pure
 * comparator behind that indicator — no store, no IPC, no React.
 *
 * Locked semantics:
 *  - F2: a tab now bound to a different database than the pin's envelope
 *    `database` has diverged, full stop — checked before any content rule,
 *    including the file-pin short-circuit below.
 *  - G1: file-backed query/notebook pins treat the bound file as the source
 *    of truth, not the pin's stored payload. Content drift between the pin's
 *    stored snapshot and whatever the file now contains is expected and
 *    never flagged — only a database rebind still diverges a file pin.
 *  - G15: notebook comparison ignores volatile per-cell fields (`id`,
 *    `isEditing`) but still catches a deep `metadata` change.
 */

import type {
  PinnedWorkspaceRef,
  PinnedTableViewPayload,
  PinnedQueryPayload,
  PinnedNotebookPayload,
  PinnedDataComparePayload,
  PinnedSchemaComparePayload,
} from '../../../../types/electron';
import type { PinnedNotebookCells } from './notebookCellSanitizer';

export type CapturedDefinition =
  | { workspaceType: 'table-explorer'; payload: PinnedTableViewPayload }
  | { workspaceType: 'query'; payload: PinnedQueryPayload }
  | { workspaceType: 'notebook'; payload: PinnedNotebookPayload }
  | { workspaceType: 'data-compare'; payload: PinnedDataComparePayload }
  | { workspaceType: 'schema-compare'; payload: PinnedSchemaComparePayload };

// ============================================================================
// stableStringify — key-order-insensitive deep JSON serialization
// ============================================================================

/**
 * Recursively sorts object keys before `JSON.stringify` so two structurally
 * equal values compare equal regardless of the key order either side was
 * built in. Array element order IS significant and left untouched — the
 * payloads compared here (`filters`, `sorting`, notebook `cells`, etc.) are
 * ordered lists where reordering is itself a real, user-visible change.
 */
function sortKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      out[key] = sortKeysDeep((value as Record<string, unknown>)[key]);
    }
    return out;
  }
  return value;
}

function stableStringify(value: unknown): string {
  return JSON.stringify(sortKeysDeep(value));
}

// ============================================================================
// tableView
// ============================================================================

/**
 * Structural fields that define a Table Explorer view. `database` is
 * excluded on purpose — it's already covered by the F2 database-rebind check
 * in `isPinDiverged`, and `navigationTree` was already stripped when the
 * payload was captured (see `PinnedTableViewPayload`).
 */
function normalizeTableView(payload: PinnedTableViewPayload) {
  const { tableName, schema, limit, selectedColumns, filters, sorting } = payload;
  return { tableName, schema, limit, selectedColumns, filters, sorting };
}

function tableViewDiverged(
  pinPayload: PinnedTableViewPayload,
  capturedPayload: PinnedTableViewPayload,
): boolean {
  return stableStringify(normalizeTableView(capturedPayload)) !== stableStringify(normalizeTableView(pinPayload));
}

// ============================================================================
// query
// ============================================================================

function queryDiverged(pinPayload: PinnedQueryPayload, capturedPayload: PinnedQueryPayload): boolean {
  // Both sides are guaranteed `source: 'text'` here — `isPinDiverged` returns
  // early (G1) before calling this for a file-source pin. A captured side
  // that's somehow still `'file'` can't be honestly compared as text, so
  // treat it conservatively as diverged rather than silently reporting clean.
  if (pinPayload.source !== 'text' || capturedPayload.source !== 'text') return true;
  return capturedPayload.query !== pinPayload.query;
}

// ============================================================================
// notebook
// ============================================================================

/** Volatile fields (`id`, `isEditing`) are dropped; the rest is compared. */
function normalizeNotebookCells(cells: PinnedNotebookCells) {
  // Explicit element-type annotation: `PinnedNotebookCells` re-exports through
  // `types/electron.d.ts`, which can silently resolve to `any` under plain
  // `tsc` — an implicit-any callback parameter here would leak that into the
  // rest of the function without the compiler ever flagging it.
  return cells.map((cell: PinnedNotebookCells[number]) => ({
    cellType: cell.cellType,
    source: cell.source,
    chartConfig: cell.chartConfig,
  }));
}

function notebookDiverged(
  pinPayload: PinnedNotebookPayload,
  capturedPayload: PinnedNotebookPayload,
): boolean {
  // Both sides are guaranteed `source: 'cells'` here — see the queryDiverged
  // comment above; same reasoning applies to file-source notebook pins.
  if (pinPayload.source !== 'cells' || capturedPayload.source !== 'cells') return true;

  if (
    stableStringify(normalizeNotebookCells(capturedPayload.cells)) !==
    stableStringify(normalizeNotebookCells(pinPayload.cells))
  ) {
    return true;
  }

  return stableStringify(capturedPayload.metadata) !== stableStringify(pinPayload.metadata);
}

// ============================================================================
// Dispatcher
// ============================================================================

/**
 * Returns `true` when the live tab's current definition (`captured`,
 * `tabDatabase`) no longer matches what `pin` has stored on disk.
 */
export function isPinDiverged(
  pin: PinnedWorkspaceRef,
  captured: CapturedDefinition,
  tabDatabase: string | undefined,
): boolean {
  // F2 — checked first, unconditionally, including for file pins.
  if (tabDatabase !== pin.database) return true;

  switch (pin.kind) {
    case 'tableView':
      return captured.workspaceType === 'table-explorer'
        ? tableViewDiverged(pin.payload, captured.payload)
        : true;

    case 'query':
      if (pin.payload.source === 'file') return false; // G1
      return captured.workspaceType === 'query' ? queryDiverged(pin.payload, captured.payload) : true;

    case 'notebook':
      if (pin.payload.source === 'file') return false; // G1
      return captured.workspaceType === 'notebook'
        ? notebookDiverged(pin.payload, captured.payload)
        : true;

    // Comparison pins (B5/F07) — the whole payload IS the configuration, and
    // it is already normalized by `captureComparePinDefinition`'s explicit
    // picks (no volatile fields to exclude the way notebook cells need), so a
    // key-order-insensitive deep compare is the honest test. The F2
    // database-rebind check above already covered a source-database change.
    case 'dataCompare':
      return captured.workspaceType === 'data-compare'
        ? stableStringify(captured.payload) !== stableStringify(pin.payload)
        : true;

    case 'schemaCompare':
      return captured.workspaceType === 'schema-compare'
        ? stableStringify(captured.payload) !== stableStringify(pin.payload)
        : true;

    default:
      // Exhaustive over PinnedWorkspaceRef['kind']; unreachable in practice.
      return true;
  }
}
